import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { MultiSelect } from 'primereact/multiselect';
import surveyService from '../../services/data/survey.service';
import companyService from '../../services/data/company.service';

const EMPTY_FORM = {
    periodo_valor: 45,
    periodo_unidade: 'days',
    data_referencia: 'admission_date',
    tipo_disparo: 'once',
    recorrencia_valor: null,
    recorrencia_unidade: 'years',
    escopo_tipo: 'all',
    escopo_ids: [],
    prazo_resposta_dias: 7,
    lembrete_dias_antes: 2,
    janela_tolerancia_dias: 0,
    apply_retroactive: false,
    lookback_days: null,
    ativo: true,
};

function errDetail(err) {
    const d = err.response?.data?.detail;
    if (Array.isArray(d)) return d.join(' ');
    if (typeof d === 'string') return d;
    return err.message || 'Erro';
}

export default function SurveyTriggerDialog({ visible, onHide, companyId, surveyId, trigger, onSaved, toastRef }) {
    const [form, setForm] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [scopeOptions, setScopeOptions] = useState([]);

    useEffect(() => {
        if (!visible) return;
        if (trigger) {
            setForm({
                periodo_valor: trigger.periodo_valor,
                periodo_unidade: trigger.periodo_unidade,
                data_referencia: trigger.data_referencia,
                tipo_disparo: trigger.tipo_disparo,
                recorrencia_valor: trigger.recorrencia_valor,
                recorrencia_unidade: trigger.recorrencia_unidade || 'years',
                escopo_tipo: trigger.escopo_tipo,
                escopo_ids: trigger.escopo_ids || [],
                prazo_resposta_dias: trigger.prazo_resposta_dias,
                lembrete_dias_antes: trigger.lembrete_dias_antes,
                janela_tolerancia_dias: trigger.janela_tolerancia_dias,
                apply_retroactive: trigger.apply_retroactive,
                lookback_days: trigger.lookback_days,
                ativo: trigger.ativo,
            });
        } else {
            setForm(EMPTY_FORM);
        }
    }, [visible, trigger]);

    useEffect(() => {
        if (!visible || !companyId) return;
        if (form.escopo_tipo === 'departamento') {
            companyService.listDepartmentSimple().then((res) => {
                setScopeOptions(res.map((d) => ({ label: d.title, value: d.id })));
            }).catch(() => setScopeOptions([]));
        } else if (form.escopo_tipo === 'filial') {
            companyService.autoCompleteSubCompany('').then((res) => {
                setScopeOptions(res.map((s) => ({ label: s.title, value: s.id })));
            }).catch(() => setScopeOptions([]));
        } else if (form.escopo_tipo === 'cargo') {
            companyService.autoCompleteJobPosition('').then((res) => {
                setScopeOptions((res || []).map((j) => ({ label: j.title, value: j.id })));
            }).catch(() => setScopeOptions([]));
        } else {
            setScopeOptions([]);
        }
    }, [visible, companyId, form.escopo_tipo]);

    function updateField(key, value) {
        setForm((prev) => ({ ...prev, [key]: value }));
    }

    function submit() {
        setSubmitting(true);
        const payload = {
            company_id: companyId,
            survey_id: surveyId,
            ...form,
            escopo_ids: form.escopo_tipo === 'all' ? [] : form.escopo_ids,
            lookback_days: form.apply_retroactive ? form.lookback_days : null,
        };
        if (form.tipo_disparo === 'once') {
            payload.recorrencia_valor = null;
            payload.recorrencia_unidade = null;
        }

        const req = trigger
            ? surveyService.updateTrigger({ ...payload, trigger_id: trigger.id })
            : surveyService.createTrigger(payload);

        req.then(() => {
            toastRef?.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Gatilho salvo.' });
            onSaved();
        })
            .catch((err) => {
                toastRef?.current?.show({ severity: 'error', summary: 'Erro', detail: errDetail(err) });
            })
            .finally(() => setSubmitting(false));
    }

    const footer = (
        <div>
            <button type="button" className="btn btn-outline-secondary btn-rounded me-2" onClick={onHide} disabled={submitting}>
                Cancelar
            </button>
            <button type="button" className="btn btn-success btn-rounded" onClick={submit} disabled={submitting}>
                {submitting ? 'Salvando…' : 'Salvar gatilho'}
            </button>
        </div>
    );

    return (
        <Dialog
            header={trigger ? 'Editar gatilho' : 'Novo gatilho'}
            visible={visible}
            style={{ width: '560px' }}
            onHide={onHide}
            footer={footer}
            dismissableMask
        >
            <p className="small text-muted mb-3">
                Salvar grava a regra no sistema. Os disparos são avaliados automaticamente todo dia às 06h
                (exceto lookback retroativo, aplicado ao salvar). Não é criado um agendamento separado por gatilho.
            </p>
            <div className="row g-3">
                <div className="col-6">
                    <label className="form-label small fw-bold">Período</label>
                    <input
                        type="number"
                        min="1"
                        className="form-control"
                        value={form.periodo_valor}
                        onChange={(e) => updateField('periodo_valor', parseInt(e.target.value, 10) || 1)}
                    />
                </div>
                <div className="col-6">
                    <label className="form-label small fw-bold">&nbsp;</label>
                    <select className="form-select" value={form.periodo_unidade} onChange={(e) => updateField('periodo_unidade', e.target.value)}>
                        <option value="days">Dias</option>
                        <option value="months">Meses</option>
                        <option value="years">Anos</option>
                    </select>
                </div>
                <div className="col-12">
                    <label className="form-label small fw-bold">Data de referência</label>
                    <select className="form-select" value={form.data_referencia} onChange={(e) => updateField('data_referencia', e.target.value)}>
                        <option value="admission_date">Data de admissão</option>
                        <option value="role_start_date">Data de início no cargo atual</option>
                    </select>
                </div>
                <div className="col-12">
                    <label className="form-label small fw-bold">Tipo de disparo</label>
                    <select className="form-select" value={form.tipo_disparo} onChange={(e) => updateField('tipo_disparo', e.target.value)}>
                        <option value="once">Uma vez</option>
                        <option value="recurring">Recorrente</option>
                    </select>
                </div>
                {form.tipo_disparo === 'recurring' && (
                    <>
                        <div className="col-6">
                            <label className="form-label small fw-bold">Recorrência</label>
                            <input
                                type="number"
                                min="1"
                                className="form-control"
                                value={form.recorrencia_valor || ''}
                                onChange={(e) => updateField('recorrencia_valor', parseInt(e.target.value, 10) || 1)}
                            />
                        </div>
                        <div className="col-6">
                            <label className="form-label small fw-bold">&nbsp;</label>
                            <select className="form-select" value={form.recorrencia_unidade} onChange={(e) => updateField('recorrencia_unidade', e.target.value)}>
                                <option value="months">Meses</option>
                                <option value="years">Anos</option>
                            </select>
                        </div>
                    </>
                )}
                <div className="col-12">
                    <label className="form-label small fw-bold">Escopo</label>
                    <select
                        className="form-select"
                        value={form.escopo_tipo}
                        onChange={(e) => updateField('escopo_tipo', e.target.value)}
                    >
                        <option value="all">Todos os colaboradores ativos</option>
                        <option value="filial">Filial</option>
                        <option value="departamento">Departamento</option>
                        <option value="cargo">Cargo</option>
                    </select>
                </div>
                {form.escopo_tipo !== 'all' && (
                    <div className="col-12">
                        <label className="form-label small fw-bold">Selecione</label>
                        <MultiSelect
                            value={form.escopo_ids}
                            options={scopeOptions}
                            onChange={(e) => updateField('escopo_ids', e.value)}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Selecione…"
                            className="w-100"
                            display="chip"
                        />
                    </div>
                )}
                <div className="col-4">
                    <label className="form-label small fw-bold">Prazo de resposta (dias)</label>
                    <input type="number" min="1" className="form-control" value={form.prazo_resposta_dias} onChange={(e) => updateField('prazo_resposta_dias', parseInt(e.target.value, 10) || 1)} />
                </div>
                <div className="col-4">
                    <label className="form-label small fw-bold">Lembrete (dias antes)</label>
                    <input type="number" min="0" className="form-control" value={form.lembrete_dias_antes} onChange={(e) => updateField('lembrete_dias_antes', parseInt(e.target.value, 10) || 0)} />
                </div>
                <div className="col-4">
                    <label className="form-label small fw-bold">Tolerância (dias úteis)</label>
                    <input type="number" min="0" className="form-control" value={form.janela_tolerancia_dias} onChange={(e) => updateField('janela_tolerancia_dias', parseInt(e.target.value, 10) || 0)} />
                </div>
                <div className="col-12">
                    <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="applyRetro" checked={form.apply_retroactive} onChange={(e) => updateField('apply_retroactive', e.target.checked)} />
                        <label className="form-check-label small" htmlFor="applyRetro">
                            Aplicar a colaboradores que já atingiram este marco (retroativo)
                        </label>
                    </div>
                </div>
                {form.apply_retroactive && (
                    <div className="col-12">
                        <label className="form-label small fw-bold">Período de corte (últimos N dias)</label>
                        <input type="number" min="1" className="form-control" value={form.lookback_days || ''} onChange={(e) => updateField('lookback_days', parseInt(e.target.value, 10) || null)} />
                    </div>
                )}
                <div className="col-12">
                    <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="triggerAtivo" checked={form.ativo} onChange={(e) => updateField('ativo', e.target.checked)} />
                        <label className="form-check-label small" htmlFor="triggerAtivo">Gatilho ativo</label>
                    </div>
                </div>
            </div>
        </Dialog>
    );
}
